import { Fragment, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate, useMatches } from 'react-router';
import { useDisclosure } from '@mantine/hooks';
import { NavLink as MantineNavLink, Space } from '@mantine/core';

import { useCanAccessNavItem } from 'hooks/useCanAccessNavItem';

import { ChevronDownIconXs } from '@chownow/pantry/icons';
const normalizePath = path => (path?.endsWith('/') ? path.slice(0, -1) : path);

export default function NavList({ navLinks }) {
  const canAccessNavItem = useCanAccessNavItem();
  const filteredNavLinks = navLinks.filter(item => canAccessNavItem(item.key));

  if (!filteredNavLinks.length) {
    return null;
  }

  return (
    <>
      {filteredNavLinks.map(item => (
        <NavList.ParentLink item={item} key={item.key} />
      ))}
    </>
  );
}

NavList.propTypes = {
  navLinks: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      links: PropTypes.arrayOf(
        PropTypes.shape({
          path: PropTypes.string,
          label: PropTypes.string,
        }),
      ),
      path: PropTypes.string,
    }).isRequired,
  ),
};

/**
 * Determines which nav item should be active based on current route.
 * Handles exact matches, parent relationships via handle.parent,
 * and nested routes without sibling conflicts.
 */
function useActiveChild() {
  const { pathname } = useLocation();
  const matches = useMatches();
  const parent = matches[matches.length - 1].handle?.parent;

  return useCallback(
    item => {
      if (item.label === parent) {
        return [item, parent];
      }
      const normalizedPathname = normalizePath(pathname);
      // Pre-compute normalized paths for performance
      const normalizedSiblingPaths = new Map(
        item.links?.map(link => [link, normalizePath(link.path)]) || [],
      );
      const activeParent = item.links?.find(link => {
        const normalizedLinkPath = normalizedSiblingPaths.get(link);
        // Exact path match
        if (normalizedLinkPath === normalizedPathname) {
          return true;
        }
        // Parent relationship via router handle
        if (link.label === parent) {
          return true;
        }
        // Nested route match (e.g., /menu/edit under /menu)
        // Only if no sibling nav item matches better
        if (normalizedPathname.startsWith(`${normalizedLinkPath}/`)) {
          const hasBetterSiblingMatch = item.links?.some(sibling => {
            if (sibling === link) return false;
            const normalizedSiblingPath = normalizedSiblingPaths.get(sibling);
            return (
              normalizedPathname === normalizedSiblingPath ||
              normalizedPathname.startsWith(`${normalizedSiblingPath}/`)
            );
          });
          return !hasBetterSiblingMatch;
        }
        return false;
      });
      return activeParent ? [activeParent, parent] : undefined;
    },
    [parent, pathname],
  );
}

NavList.ParentLink = function ParentLink({ item }) {
  const { pathname } = useLocation();
  const getActiveChild = useActiveChild();
  const activeChild = getActiveChild(item);

  const navigate = useNavigate();
  const [opened, handlers] = useDisclosure(!!activeChild);
  const canAccessNavItem = useCanAccessNavItem();

  const onClick = itemPath => {
    // to refresh orders list
    if (
      pathname === itemPath &&
      pathname.includes('/orders/') &&
      !pathname.includes('/3rd-party-orders')
    ) {
      window.location.reload();
    } else {
      navigate(itemPath);
    }
  };

  useEffect(() => {
    const defaultOpened = !!getActiveChild(item);

    if (defaultOpened) {
      handlers.open();
    }
    // Do not close the menu when navigating between children—only open it when needed.
  }, [getActiveChild, item]);

  const renderSubLinks = (itemLinks = []) => {
    const filteredSubLinks = itemLinks.filter(subNav => canAccessNavItem(subNav.key));

    if (!filteredSubLinks.length) {
      return null;
    }

    return (
      <>
        {filteredSubLinks.map(subNav => (
          <Fragment key={subNav.label}>
            <Space h="xs2" />
            <MantineNavLink
              label={subNav.label}
              component="div"
              onClick={() => onClick(subNav.path)}
              active={
                pathname === subNav.path ||
                `${pathname}/` === subNav.path ||
                subNav === activeChild?.[0]
              }
            />
          </Fragment>
        ))}
      </>
    );
  };

  const subNavLinks = renderSubLinks(item.links);

  // a parent folder without any valid children
  if (!item.path && !subNavLinks) {
    return null;
  }

  // Parent is active if pathname matches exactly (useActiveChild handles nested routes)
  const isParentActive = normalizePath(item.path) === normalizePath(pathname);

  return (
    <Fragment key={item.key}>
      <MantineNavLink
        label={item.label}
        component="div"
        rightSection={item.path ? null : <ChevronDownIconXs />}
        opened={opened}
        onClick={() => (item.path ? onClick(item.path) : handlers.toggle())}
        active={isParentActive}
      >
        {renderSubLinks(item.links)}
      </MantineNavLink>
      <Space h="sm" />
    </Fragment>
  );
};

NavList.ParentLink.propTypes = {
  item: PropTypes.shape().isRequired,
};
